import {createSelector} from 'reselect'
import {getInitialWebVars} from 'powtoon-commons/utils/webvars'
import {MILLISECONDS_IN_DAY} from 'powtoon-commons/utils/datetime'
import {CurrentUserMapExtension, LIMITED_FEATURES, UserPermissions} from '../types'

const EMPTY_OBJECT = {}

export const getCurrentUser = <T extends CurrentUserMapExtension>(state: T) => state['powtoon@@currentUser']

export const getCurrentUserProfile = <T extends CurrentUserMapExtension>(state: T) => getCurrentUser(state).userProfile
export const getCurrentUserProfileData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).userProfile.data
export const getCurrentUserAccountDate = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserProfileData(state).accountDate
export const getCurrentUserLastLoginDate = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserProfileData(state).lastLoginDate
export const getCurrentUserPreviousLoginDate = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserProfileData(state).previousLoginDate
export const getCurrentUserId = <T extends CurrentUserMapExtension>(state: T) => getCurrentUserProfileData(state).id
export const getCurrentUserBillingInfoData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserBillingInfo(state).data
export const getCurrentUserBillingInfo = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).billingInfo

export const getCurrentUserPermissions = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).permissions
export const getCurrentUserPermissionsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).permissions.data
export const getCurrentUserPermissionsLoaded = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).permissions.loaded
export const getCurrentUserTrialEndDate = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).trialEndDate
export const getCurrentUserTrialEndDateEc = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).trialEndDateEc
export const getCurrentUserStudioPowtoonsCount = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).studioPowtoonsCount
export const getCurrentUserStudioPowtoonsLimit = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).studioPowtoonsLimit
export const getCurrentUserStudioMaxVideoDuration = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).maxVideoTrackDuration

export const getCurrentUserLimitedFeatures = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserPermissionsData(state).limitedFeatures

export const getCurrentUserPlan = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).currentPlan

export const canGenerateProducerPowtoonFromUserPermissions =
    ({canUseAiVideoProducer, limitedFeatures}: UserPermissions) => {
      const aiLimitedFeatures = limitedFeatures?.[LIMITED_FEATURES.AI]
      const available = aiLimitedFeatures?.available ?? 0
      const hasTryouts = available > 0
      if (canUseAiVideoProducer === null) {
        return hasTryouts
      }
      return !!canUseAiVideoProducer
    }

export const getCanGenerateProducerPowtoon = createSelector(
  getCurrentUserPermissionsData,
  (currentUserPermissionsData) => {
    return canGenerateProducerPowtoonFromUserPermissions(currentUserPermissionsData)
  }
)

export const getCurrentUserRecommendations = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).recommendations
export const getCurrentUserRecommendationsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserRecommendations(state).data

export const getCurrentUserDashboardSettings = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).settings.dashboard
export const getCurrentUserDashboardSettingsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserDashboardSettings(state).data

export const getOnboardingIsStarted = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserDashboardSettingsData(state).onboardingIsStarted

export const getStoryTellingOnboardingIsFinished = <T extends CurrentUserMapExtension>(state: T) => {
  const {
    loaded,
    data: {onboardingIsStarted, selectedVideoTypeId}
  } = getCurrentUserDashboardSettings(state)
  return loaded && onboardingIsStarted && selectedVideoTypeId
}

export const getCurrentUserDashboardSettingsAcknowledgedPowIds = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserDashboardSettingsData(state).acknowledgeData || []

export const getPowtoonCaptureNotifications = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserDashboardSettingsData(state).notifications.powtoonCapture

export const getCurrentUserStudioSettings = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).settings.studio
export const getCurrentUserStudioSettingsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettings(state).data

export const getCurrentUserCharacterBuilderSettings = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).settings.characterBuilder
export const getCurrentUserCharacterBuilderSettingsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserCharacterBuilderSettings(state).data

// GROUPS
export const getCurrentUserOrgRolesInGroups = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).userOrgRolesInGroups
export const getCurrentUserOrgRolesInGroupsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserOrgRolesInGroups(state).results

export const getCurrentUserOrgPermissions = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUser(state).userOrgPermissions
export const getCurrentUserOrgPermissionsData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserOrgPermissions(state).data

export const getCurrentUserHasGroups = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserOrgRolesInGroupsData(state) && getCurrentUserOrgRolesInGroupsData(state).length > 0

// STUDIO SETTINGS
export const getMyColors = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).favorites.colors
export const getTrialActivatedMessageSeen = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).trialActivatedMessageSeen
export const getIsLiked = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).isLiked
export const getIsQuickMenuShown = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).isQuickMenuShown
export const getTips = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).tips || EMPTY_OBJECT
export const getSettingsTipsVersion = <T extends CurrentUserMapExtension>(state: T) => getTips(state).version
export const getUserSettingsPreferences = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).preferences || EMPTY_OBJECT
export const getAutoConfirmValue = <T extends CurrentUserMapExtension>(state: T, valueName: string) =>
  !!(getUserSettingsPreferences(state) as any)[valueName]
export const getCurrentUserFavoriteFonts = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).favorites.fonts
export const getRevisionModeNotificationData = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).notifications?.revisionMode
export const getLastSelectedPalette = <T extends CurrentUserMapExtension>(state: T) =>
  getCurrentUserStudioSettingsData(state).colorPicker?.lastColorPalette
export const getIsUserOverStudioPowtoonsLimit = createSelector(
  getCurrentUserStudioPowtoonsCount,
  getCurrentUserStudioPowtoonsLimit,
  (studioPowtoonsCount, studioPowtoonsLimit) =>
    typeof studioPowtoonsLimit === 'number' &&
    typeof studioPowtoonsCount === 'number' &&
    studioPowtoonsLimit - studioPowtoonsCount <= 0
)

export const getIsUserInFreeTrial = createSelector(
  getCurrentUserTrialEndDate,
  currentUserTrialEndDate => !!currentUserTrialEndDate && currentUserTrialEndDate > new Date()
)

export const getTrialEndDateGeneric = createSelector(
  getCurrentUserTrialEndDate,
  getCurrentUserTrialEndDateEc,
  (trialEndDate, trialEndDateEc) => {
    const {is_enterprise: isEnterprise} = getInitialWebVars()
    const currentUserTrialEndDate = isEnterprise ? trialEndDateEc : trialEndDate
    return currentUserTrialEndDate
  }
)

export const getIsUserInFreeTrialGeneric = createSelector(
  getTrialEndDateGeneric,
  (trialEndDate) => !!trialEndDate && trialEndDate > new Date()
)

export const getIsFormSubmittedInLastMonth = createSelector(
  getCurrentUserDashboardSettings,
  ({loading, loaded, data}) => {
    const month = MILLISECONDS_IN_DAY * 30
    const now = Date.now()
    const pqlFormsSubmittedInLastMonth: Record<string, boolean|number> = {}
    for (const form in data.pqlFormsSubmissionDates) {
      if (form) {
        const formDate = data.pqlFormsSubmissionDates[form]
        pqlFormsSubmittedInLastMonth[form] = formDate && now - formDate < month
      }
    }
    return {
      loading,
      loaded,
      pqlFormsSubmittedInLastMonth,
      isTeamsFormSubmittedInLastMonth: (
        data.teamsContactFormSubmissionDate &&
        now - data.teamsContactFormSubmissionDate < month
      ),
      isManageBrandFormSubmittedInLastMonth: (
        data.manageBrandFormSubmissionDate &&
        now - data.manageBrandFormSubmissionDate < month
      ),
      isBookDemoFormSubmittedInLastMonth: (
        data.bookDemoFormSubmissionDate &&
        now - data.bookDemoFormSubmissionDate < month
      )
    }
  }
)
